// Forgestus MCP server catalog — shared constant (Option A from mcp-server-ux-plan.md).
//
// Consumed directly by McpServersTab.tsx. Structured so it can later be served
// from a Forge backend endpoint (Option B) without changing the consumer shape.
//
// Provenance is displayed as a quiet text label — never an external hyperlink.

import type { McpTransport } from './projects';

export type McpCatalogCategory =
  | 'aws'
  | 'dev-tools'
  | 'iac'
  | 'security'
  | 'data'
  | 'observability'
  | 'productivity'
  | 'web';

export const CATALOG_CATEGORY_LABELS: Record<McpCatalogCategory, string> = {
  aws:           'AWS',
  'dev-tools':   'Dev Tools',
  iac:           'Infrastructure',
  security:      'Security',
  data:          'Data',
  observability: 'Observability',
  productivity:  'Productivity',
  web:           'Web',
};

// ---------------------------------------------------------------------------
// Friendly connection fields (FORGE-166).
//
// Instead of making users edit raw command/args/env, each catalog server
// declares the human inputs it actually needs (a database URL, an API token,
// an AWS role). The modal renders these as labelled inputs with help text and
// assembles the underlying McpServer behind the scenes (see shared/lib/mcpAssemble).
// ---------------------------------------------------------------------------

/** Where a field's value lands in the assembled McpServer. */
export type McpFieldBind =
  // Injected as an environment variable into the server process (stdio).
  | { into: 'env'; envKey: string }
  // Bearer token for sse/http transports. A value that looks like a Secrets
  // Manager ARN is stored as `auth_secret_arn` (resolved securely at launch);
  // anything else is stored as a plaintext `auth_token`.
  | { into: 'authToken' }
  // Cross-account AWS (FORGE-164).
  | { into: 'awsRole' }
  | { into: 'awsRegion' }
  // File-based credential (FORGE-169). The value is file CONTENT (e.g. a
  // kubeconfig YAML) that the agent materializes to a temp file at launch and
  // points the server's env var at — for servers that read a FILE PATH, not an
  // env value. Currently only kubeconfig → KUBECONFIG.
  | { into: 'kubeconfig' };

export interface McpConfigField {
  /** Stable key — form state + data-testid suffix (`mcp-field-<key>`). */
  key: string;
  /** Human label, e.g. "Database connection string". */
  label: string;
  /** How the value maps into the server config. */
  bind: McpFieldBind;
  placeholder?: string;
  /** Guidance shown beneath the input — formats, gotchas, where to find it. */
  help?: string;
  /** Render as a masked input and never echo back. */
  secret?: boolean;
  /** Block save until provided. */
  required?: boolean;
  /** Render as a multi-line textarea (e.g. a pasted kubeconfig). */
  multiline?: boolean;
}

export interface McpCatalogEntry {
  /** Stable slug — used as the data-testid suffix and deduplication key. */
  id: string;
  label: string;
  /** One-line description shown on the catalog card. */
  description: string;
  category: McpCatalogCategory;
  /** Emoji or short text icon shown on the card. */
  icon: string;
  /** The server config this entry expands to when the user clicks Add. */
  template: {
    suggestedName: string;
    transport: McpTransport;
    command?: string;
    args?: string[];
    url?: string;
    /**
     * Friendly inputs the user fills in. The modal renders these (hiding raw
     * command/args/transport) and the assembler maps them into the server.
     */
    fields?: McpConfigField[];
    /** Fixed, non-secret env applied automatically (e.g. a default region). */
    defaultEnv?: Record<string, string>;
    /** True when the server needs a secret/token — drives the "Auth required" chip. */
    requiresAuth?: boolean;
    authLabel?: string;
  };
  /** Pre-installed in the Forge base image — cold start is instant. */
  preinstalled?: boolean;
  /**
   * Minimum plan required above Team. Omit or set to 'team' for all Team+.
   * Uses the same ProjectPlan type but we avoid the import cycle by keeping
   * it as a string literal — McpServersTab normalises with PLANS.
   */
  minPlan?: 'team' | 'business' | 'enterprise';
  /**
   * Quiet provenance label shown in the card footer.
   * Never rendered as a hyperlink.
   */
  source?: string;
}

// ---------------------------------------------------------------------------
// Seed catalog — client-resource servers only. Non-client servers (git,
// filesystem, fetch — operate on Forge's agent container, not client resources;
// redundant with agents' native shell/file tools) and aws-documentation (public
// docs, no client data) were removed: the catalog offers only servers that
// connect to a CLIENT's own account/service. Verify package names before release.
// ---------------------------------------------------------------------------

export const MCP_CATALOG: McpCatalogEntry[] = [
  // --- AWS -----------------------------------------------------------------
  // AWS-backed servers collect a client IAM role in the modal's "Your AWS
  // account" section (FORGE-164) rather than a field, so they need no secret
  // field of their own.
  {
    id: 'bedrock-kb',
    label: 'Bedrock Knowledge Base',
    description: 'Query your Bedrock Knowledge Bases — index runbooks, postmortems, and architecture docs.',
    category: 'aws',
    icon: '🧠',
    preinstalled: true,
    source: 'AWS Labs',
    template: {
      suggestedName: 'bedrock-kb',
      transport: 'stdio',
      command: 'uvx',
      args: ['awslabs.bedrock-kb-retrieval-mcp-server@latest'],
      defaultEnv: { AWS_REGION: 'us-east-1' },
      fields: [
        {
          key: 'kb_tag',
          label: 'Knowledge Base inclusion tag (optional)',
          bind: { into: 'env', envKey: 'KB_INCLUSION_TAG_KEY' },
          placeholder: 'forge-runbook',
          help: 'Only Knowledge Bases tagged with this key are exposed. Leave blank to allow all in the account.',
        },
      ],
    },
  },

  // --- Observability -------------------------------------------------------
  {
    id: 'cloudwatch',
    label: 'CloudWatch Logs & Metrics',
    description: 'Run Logs Insights queries, fetch metric data, and inspect CloudWatch alarms.',
    category: 'observability',
    icon: '📊',
    preinstalled: true,
    source: 'AWS Labs',
    template: {
      suggestedName: 'cloudwatch',
      transport: 'stdio',
      command: 'uvx',
      args: ['awslabs.cloudwatch-mcp-server@latest'],
      defaultEnv: { AWS_REGION: 'us-east-1' },
    },
  },
  {
    id: 'sentry',
    label: 'Sentry',
    description: 'Query Sentry issues, stack traces, and error events from your projects.',
    category: 'observability',
    icon: '🐛',
    source: 'Sentry',
    template: {
      suggestedName: 'sentry',
      transport: 'http',
      url: 'https://mcp.sentry.io/mcp',
      requiresAuth: true,
      authLabel: 'Sentry auth token',
      fields: [
        {
          key: 'token',
          label: 'Sentry auth token',
          bind: { into: 'authToken' },
          secret: true,
          required: true,
          placeholder: 'sntrys_… or a Secrets Manager ARN',
          help: 'Sentry → Settings → Auth Tokens → Create. Paste the token, or paste a Secrets Manager ARN to store it securely.',
        },
      ],
    },
  },
  {
    id: 'grafana',
    label: 'Grafana',
    description: 'Query dashboards, datasources, Prometheus/Loki data, alert rules, and incidents in your Grafana.',
    category: 'observability',
    icon: '📈',
    preinstalled: true,
    source: 'Grafana Labs',
    template: {
      suggestedName: 'grafana',
      transport: 'stdio',
      // Pre-baked Go binary (release artifact) on PATH; stdio is the default.
      command: 'mcp-grafana',
      args: [],
      requiresAuth: true,
      authLabel: 'Grafana service account token',
      fields: [
        {
          key: 'grafana_url',
          label: 'Grafana URL',
          bind: { into: 'env', envKey: 'GRAFANA_URL' },
          required: true,
          placeholder: 'https://your-org.grafana.net',
          help: "Your Grafana instance URL — a Cloud stack URL or a self-hosted host. Must be reachable from Forge's cloud, not localhost.",
        },
        {
          key: 'grafana_token',
          label: 'Grafana service account token',
          bind: { into: 'env', envKey: 'GRAFANA_SERVICE_ACCOUNT_TOKEN' },
          secret: true,
          required: true,
          placeholder: 'glsa_…',
          help: 'Grafana → Administration → Service accounts → Add service account token. Grant only the RBAC roles (e.g. Viewer) the tools you use require.',
        },
      ],
    },
  },

  // --- Dev Tools -----------------------------------------------------------
  {
    id: 'github',
    label: 'GitHub',
    description: 'Read repos, issues, pull requests, and files from GitHub.',
    category: 'dev-tools',
    icon: '🐙',
    source: 'GitHub',
    template: {
      suggestedName: 'github',
      transport: 'http',
      url: 'https://api.githubcopilot.com/mcp/',
      requiresAuth: true,
      authLabel: 'GitHub personal access token (PAT)',
      fields: [
        {
          key: 'token',
          label: 'GitHub personal access token (PAT)',
          bind: { into: 'authToken' },
          secret: true,
          required: true,
          placeholder: 'github_pat_… or a Secrets Manager ARN',
          help: 'GitHub → Settings → Developer settings → Personal access tokens. Read-only repo scope is enough. Paste the token, or a Secrets Manager ARN to store it securely.',
        },
      ],
    },
  },

  {
    id: 'gitlab',
    label: 'GitLab',
    description: 'Review GitLab merge requests and issues — fetch details, diffs, and post inline review comments.',
    category: 'dev-tools',
    icon: '🦊',
    preinstalled: true,
    source: 'kopfrechner',
    template: {
      suggestedName: 'gitlab',
      transport: 'stdio',
      // Pre-baked into the Forge image (git-cloned + npm install) and launched
      // by absolute path — the upstream package isn't on npm and ships no `bin`,
      // so `node index.js` is the documented invocation.
      command: 'node',
      args: ['/opt/mcp/gitlab-mr-mcp/index.js'],
      requiresAuth: true,
      authLabel: 'GitLab access token',
      fields: [
        {
          key: 'gitlab_token',
          label: 'GitLab access token',
          bind: { into: 'env', envKey: 'MR_MCP_GITLAB_TOKEN' },
          secret: true,
          required: true,
          placeholder: 'glpat-…',
          help: 'GitLab → Settings → Access Tokens. Scopes: api, read_api. A read-only project or group access token is enough for reviewing merge requests.',
        },
        {
          key: 'gitlab_host',
          label: 'GitLab host URL',
          bind: { into: 'env', envKey: 'MR_MCP_GITLAB_HOST' },
          required: true,
          placeholder: 'https://gitlab.com',
          help: "Your GitLab instance — https://gitlab.com for SaaS, or your self-managed host. Must be reachable from Forge's cloud, not localhost.",
        },
        {
          key: 'project_search_term',
          label: 'Project search term (optional)',
          bind: { into: 'env', envKey: 'MR_MCP_PROJECT_SEARCH_TERM' },
          placeholder: 'my-team',
          help: 'Restrict the projects the server can see to those whose path or name matches this term. Leave blank to allow all accessible projects.',
        },
      ],
    },
  },

  // --- Infrastructure as Code ----------------------------------------------
  {
    id: 'terraform',
    label: 'Terraform',
    description: 'Search the Terraform Registry for providers and modules, and manage HCP Terraform / Enterprise workspaces.',
    category: 'iac',
    icon: '🏗️',
    preinstalled: true,
    source: 'HashiCorp',
    template: {
      suggestedName: 'terraform',
      transport: 'stdio',
      // Pre-baked Go binary (release artifact) on PATH; `stdio` selects the
      // local transport. Public Registry works with no credentials.
      command: 'terraform-mcp-server',
      args: ['stdio'],
      fields: [
        {
          key: 'tfe_token',
          label: 'HCP Terraform / Enterprise token (optional)',
          bind: { into: 'env', envKey: 'TFE_TOKEN' },
          secret: true,
          placeholder: 'Leave blank for the public registry',
          help: 'Only needed to reach your private registry and workspaces. Create a user or team API token in HCP Terraform → Account settings → Tokens. Blank = public Terraform Registry only.',
        },
        {
          key: 'tfe_address',
          label: 'HCP Terraform / Enterprise address (optional)',
          bind: { into: 'env', envKey: 'TFE_ADDRESS' },
          placeholder: 'https://app.terraform.io',
          help: "Defaults to https://app.terraform.io. Set to your Terraform Enterprise hostname for a self-hosted install. Must be reachable from Forge's cloud.",
        },
      ],
    },
  },
  {
    id: 'kubernetes',
    label: 'Kubernetes',
    description: 'Inspect a Kubernetes or OpenShift cluster read-only — list resources, read pods, logs, events, and config.',
    category: 'iac',
    icon: '☸️',
    preinstalled: true,
    source: 'containers (Red Hat)',
    template: {
      suggestedName: 'kubernetes',
      transport: 'stdio',
      // Pre-baked via `npm install -g kubernetes-mcp-server`. --read-only hides
      // every tool not marked ReadOnly, so the agent can't mutate the cluster.
      command: 'kubernetes-mcp-server',
      args: ['--read-only'],
      requiresAuth: true,
      authLabel: 'kubeconfig',
      fields: [
        {
          key: 'kubeconfig',
          label: 'kubeconfig',
          bind: { into: 'kubeconfig' },
          secret: true,
          required: true,
          multiline: true,
          placeholder: 'apiVersion: v1\nkind: Config\nclusters:\n- cluster:\n    server: https://your-cluster:6443\n...',
          help: "Paste a kubeconfig for a read-only ServiceAccount scoped to what the agent should see. The cluster API server must be reachable from Forge's cloud. Stored on the project settings row; prefer a short-lived, least-privilege token.",
        },
      ],
    },
  },

  // --- Security ------------------------------------------------------------
  {
    id: 'semgrep',
    label: 'Semgrep',
    description: 'Scan code for security vulnerabilities with Semgrep, and pull findings from your Semgrep AppSec Platform.',
    category: 'security',
    icon: '🔒',
    preinstalled: true,
    source: 'Semgrep',
    template: {
      suggestedName: 'semgrep',
      transport: 'stdio',
      command: 'uvx',
      args: ['semgrep-mcp'],
      // No token needed for local scanning of the agent's checkout; a token adds
      // Semgrep AppSec Platform findings (your org's rules + triaged results).
      fields: [
        {
          key: 'semgrep_token',
          label: 'Semgrep App token (optional)',
          bind: { into: 'env', envKey: 'SEMGREP_APP_TOKEN' },
          secret: true,
          placeholder: 'Leave blank for local rule scanning',
          help: 'Only needed to connect to Semgrep AppSec Platform. Semgrep → Settings → Tokens → API. Blank = local scanning with the public ruleset.',
        },
      ],
    },
  },

  // --- Data ----------------------------------------------------------------
  {
    id: 'redis',
    label: 'Redis',
    description: 'Manage and search data in Redis — strings, hashes, lists, sets, sorted sets, streams, and Pub/Sub.',
    category: 'data',
    icon: '🔴',
    preinstalled: true,
    source: 'Redis',
    template: {
      suggestedName: 'redis',
      transport: 'stdio',
      command: 'uvx',
      args: ['redis-mcp-server'],
      requiresAuth: true,
      authLabel: 'Redis connection',
      fields: [
        {
          key: 'redis_host',
          label: 'Redis host',
          bind: { into: 'env', envKey: 'REDIS_HOST' },
          required: true,
          placeholder: 'redis.example.com',
          help: "Your Redis host — a managed endpoint (Redis Cloud, ElastiCache, Upstash) or self-hosted. Must be reachable from Forge's cloud, not localhost.",
        },
        {
          key: 'redis_port',
          label: 'Redis port (optional)',
          bind: { into: 'env', envKey: 'REDIS_PORT' },
          placeholder: '6379',
          help: 'Defaults to 6379.',
        },
        {
          key: 'redis_password',
          label: 'Redis password (optional)',
          bind: { into: 'env', envKey: 'REDIS_PWD' },
          secret: true,
          placeholder: 'Leave blank if Redis has no auth',
          help: 'Your Redis AUTH password. Leave blank for unauthenticated Redis (dev only). Managed clouds always require a password.',
        },
        {
          key: 'redis_username',
          label: 'Redis username (optional)',
          bind: { into: 'env', envKey: 'REDIS_USERNAME' },
          placeholder: 'default',
          help: "Only needed for Redis 6+ ACL-based auth. The default user is 'default'. Leave blank for password-only auth.",
        },
      ],
    },
  },
  {
    id: 'clickhouse',
    label: 'ClickHouse',
    description: 'Run SQL queries and inspect schemas in your ClickHouse database — the official ClickHouse MCP server.',
    category: 'data',
    icon: '🖱️',
    preinstalled: true,
    source: 'ClickHouse',
    template: {
      suggestedName: 'clickhouse',
      transport: 'stdio',
      command: 'uvx',
      args: ['mcp-clickhouse'],
      requiresAuth: true,
      authLabel: 'ClickHouse credentials',
      fields: [
        {
          key: 'clickhouse_host',
          label: 'ClickHouse host',
          bind: { into: 'env', envKey: 'CLICKHOUSE_HOST' },
          required: true,
          placeholder: 'abc123.us-east-1.aws.clickhouse.cloud',
          help: "Your ClickHouse hostname — Cloud endpoint or self-hosted. Must be reachable from Forge's cloud, not localhost. For Cloud, copy the hostname from the Connect dialog.",
        },
        {
          key: 'clickhouse_user',
          label: 'ClickHouse user',
          bind: { into: 'env', envKey: 'CLICKHOUSE_USER' },
          required: true,
          placeholder: 'default',
          help: 'Use a read-only user with minimum privileges. Avoid the default admin user.',
        },
        {
          key: 'clickhouse_password',
          label: 'ClickHouse password',
          bind: { into: 'env', envKey: 'CLICKHOUSE_PASSWORD' },
          secret: true,
          required: true,
          placeholder: 'Your ClickHouse password',
          help: 'Password for the ClickHouse user. Paste a Secrets Manager ARN to store it securely.',
        },
        {
          key: 'clickhouse_port',
          label: 'Port (optional)',
          bind: { into: 'env', envKey: 'CLICKHOUSE_PORT' },
          placeholder: '8443',
          help: 'Defaults to 8443 (HTTPS). Use 8123 for plain HTTP (not recommended in production).',
        },
        {
          key: 'clickhouse_secure',
          label: 'Use TLS (optional)',
          bind: { into: 'env', envKey: 'CLICKHOUSE_SECURE' },
          placeholder: 'true',
          help: "Set to 'true' (default for Cloud, required) or 'false' for self-hosted HTTP. ClickHouse Cloud always requires TLS.",
        },
      ],
    },
  },
  {
    id: 'snowflake',
    label: 'Snowflake',
    description: 'Query Snowflake data and manage objects via the official Snowflake Labs MCP server — SQL execution, schema inspection, and object management.',
    category: 'data',
    icon: '❄️',
    preinstalled: true,
    source: 'Snowflake Labs',
    template: {
      suggestedName: 'snowflake',
      transport: 'stdio',
      command: 'uvx',
      // A minimal service config is baked into the Forge image at
      // /opt/mcp/snowflake/config.yaml (enables query_manager + object_manager;
      // Cortex AI tools are off by default — they require dedicated Cortex
      // Search/Analyst/Agent setup in the client's Snowflake account).
      args: ['snowflake-labs-mcp', '--service-config-file', '/opt/mcp/snowflake/config.yaml'],
      requiresAuth: true,
      authLabel: 'Snowflake credentials',
      fields: [
        {
          key: 'snowflake_account',
          label: 'Snowflake account identifier',
          bind: { into: 'env', envKey: 'SNOWFLAKE_ACCOUNT' },
          required: true,
          placeholder: 'xy12345.us-east-1',
          help: "Your Snowflake account identifier — find it in the URL: https://<account>.snowflakecomputing.com. Include the region suffix for non-default regions (e.g. xy12345.us-east-1.aws).",
        },
        {
          key: 'snowflake_user',
          label: 'Snowflake user',
          bind: { into: 'env', envKey: 'SNOWFLAKE_USER' },
          required: true,
          placeholder: 'FORGE_AGENT',
          help: 'Use a dedicated, least-privilege user. Grant only SELECT on the schemas agents need to query.',
        },
        {
          key: 'snowflake_password',
          label: 'Snowflake password',
          bind: { into: 'env', envKey: 'SNOWFLAKE_PASSWORD' },
          secret: true,
          required: true,
          placeholder: 'Your Snowflake password',
          help: 'Password for the Snowflake user. Paste a Secrets Manager ARN to store it securely.',
        },
        {
          key: 'snowflake_database',
          label: 'Default database (optional)',
          bind: { into: 'env', envKey: 'SNOWFLAKE_DATABASE' },
          placeholder: 'MY_DATABASE',
          help: 'Database to use by default. Agents can still query other databases they have access to.',
        },
        {
          key: 'snowflake_warehouse',
          label: 'Warehouse (optional)',
          bind: { into: 'env', envKey: 'SNOWFLAKE_WAREHOUSE' },
          placeholder: 'COMPUTE_WH',
          help: "Virtual warehouse for query execution. Uses the user's default warehouse if not set.",
        },
      ],
    },
  },
  {
    id: 'mongodb',
    label: 'MongoDB',
    description: 'Query and manage MongoDB collections, run aggregation pipelines, and inspect indexes — the mongodb-lens MCP server.',
    category: 'data',
    icon: '🍃',
    preinstalled: true,
    source: 'furey/mongodb-lens',
    template: {
      suggestedName: 'mongodb',
      transport: 'stdio',
      // Pre-baked globally as `mongodb-lens` binary. Connection string read from
      // CONFIG_MONGO_URI env (never argv) so the secret doesn't land in the
      // process table.
      command: 'mongodb-lens',
      args: [],
      requiresAuth: true,
      authLabel: 'MongoDB connection string',
      fields: [
        {
          key: 'mongodb_uri',
          label: 'MongoDB connection string',
          bind: { into: 'env', envKey: 'CONFIG_MONGO_URI' },
          secret: true,
          required: true,
          placeholder: 'mongodb://user:password@host:27017/dbname',
          help: "Your MongoDB connection string — Atlas, DocumentDB, or self-hosted. Must be reachable from Forge's cloud, not localhost. Atlas: copy from Connect → Drivers. Append ?authSource=admin if using a custom auth database.",
        },
        {
          key: 'default_db',
          label: 'Default database (optional)',
          bind: { into: 'env', envKey: 'CONFIG_DEFAULT_DB_NAME' },
          placeholder: 'mydb',
          help: 'Database to use when no database is specified in queries. Leave blank to require the agent to specify the database per query.',
        },
      ],
    },
  },
  {
    id: 'mysql',
    label: 'MySQL',
    description: 'Run read-only SQL queries and inspect schemas in your MySQL or MariaDB database.',
    category: 'data',
    icon: '🐬',
    preinstalled: true,
    source: 'benborla',
    template: {
      suggestedName: 'mysql',
      transport: 'stdio',
      // Pre-installed globally in the Forge image; launched via `npx -y` which
      // resolves to the local cached package (no registry download at runtime).
      command: 'npx',
      args: ['-y', '@benborla29/mcp-server-mysql'],
      requiresAuth: true,
      authLabel: 'MySQL credentials',
      fields: [
        {
          key: 'mysql_host',
          label: 'MySQL host',
          bind: { into: 'env', envKey: 'MYSQL_HOST' },
          required: true,
          placeholder: 'db.example.com',
          help: "Your MySQL or MariaDB host — RDS, PlanetScale, Aiven, or self-hosted. Must be reachable from Forge's cloud, not localhost.",
        },
        {
          key: 'mysql_port',
          label: 'Port (optional)',
          bind: { into: 'env', envKey: 'MYSQL_PORT' },
          placeholder: '3306',
          help: 'Defaults to 3306.',
        },
        {
          key: 'mysql_user',
          label: 'MySQL user',
          bind: { into: 'env', envKey: 'MYSQL_USER' },
          required: true,
          placeholder: 'forge_reader',
          help: 'Use a dedicated, read-only user. Grant only SELECT on the schemas agents need to query.',
        },
        {
          key: 'mysql_pass',
          label: 'MySQL password',
          bind: { into: 'env', envKey: 'MYSQL_PASS' },
          secret: true,
          required: true,
          placeholder: 'Your MySQL password',
          help: 'Password for the MySQL user. Paste a Secrets Manager ARN to store it securely.',
        },
        {
          key: 'mysql_db',
          label: 'Default database (optional)',
          bind: { into: 'env', envKey: 'MYSQL_DB' },
          placeholder: 'myapp',
          help: 'Default database to use. Leave blank to query across all databases the user can access.',
        },
      ],
    },
  },
  {
    id: 'postgres',
    label: 'Postgres',
    description: 'Run read-only SQL, inspect schema, and analyze performance against a Postgres database.',
    category: 'data',
    icon: '🐘',
    preinstalled: true,
    source: 'Crystal DBA',
    template: {
      suggestedName: 'postgres',
      transport: 'stdio',
      command: 'uvx',
      // crystaldba/postgres-mcp — reads the connection string from DATABASE_URI,
      // so the secret never lands in argv. restricted = read-only, safe ops only.
      args: ['postgres-mcp', '--access-mode=restricted'],
      requiresAuth: true,
      authLabel: 'Database connection string',
      fields: [
        {
          key: 'database_uri',
          label: 'Database connection string',
          bind: { into: 'env', envKey: 'DATABASE_URI' },
          secret: true,
          required: true,
          placeholder: 'postgresql://user:password@host:5432/dbname?sslmode=require',
          help: "Host must be reachable from Forge's cloud — a public endpoint or a network path into your VPC, not localhost. Use a read-only user. Append ?sslmode=require for managed Postgres (RDS, Supabase, Neon).",
        },
      ],
    },
  },

  // --- Productivity --------------------------------------------------------
  {
    id: 'atlassian',
    label: 'Jira & Confluence',
    description: 'Search and manage Jira issues and Confluence pages — JQL/CQL search, create, update, and transition.',
    category: 'productivity',
    icon: '🧩',
    preinstalled: true,
    source: 'sooperset',
    template: {
      suggestedName: 'atlassian',
      transport: 'stdio',
      command: 'uvx',
      args: ['mcp-atlassian'],
      requiresAuth: true,
      authLabel: 'Atlassian API token',
      // Configure Jira, Confluence, or both. Cloud uses email + API token; for
      // Server/Data Center use a personal access token via the custom-server
      // path (JIRA_PERSONAL_TOKEN / CONFLUENCE_PERSONAL_TOKEN).
      fields: [
        {
          key: 'jira_url',
          label: 'Jira URL',
          bind: { into: 'env', envKey: 'JIRA_URL' },
          placeholder: 'https://your-company.atlassian.net',
          help: "Your Jira Cloud site URL. Leave blank to disable Jira. Must be reachable from Forge's cloud.",
        },
        {
          key: 'jira_username',
          label: 'Jira account email',
          bind: { into: 'env', envKey: 'JIRA_USERNAME' },
          placeholder: 'you@company.com',
          help: 'The Atlassian account email that owns the API token (Cloud).',
        },
        {
          key: 'jira_api_token',
          label: 'Jira API token',
          bind: { into: 'env', envKey: 'JIRA_API_TOKEN' },
          secret: true,
          placeholder: 'Atlassian API token',
          help: 'Create at id.atlassian.com → Security → API tokens. Stored as a plaintext env var on the project settings row.',
        },
        {
          key: 'confluence_url',
          label: 'Confluence URL',
          bind: { into: 'env', envKey: 'CONFLUENCE_URL' },
          placeholder: 'https://your-company.atlassian.net/wiki',
          help: 'Your Confluence Cloud URL — note the /wiki suffix. Leave blank to disable Confluence.',
        },
        {
          key: 'confluence_username',
          label: 'Confluence account email',
          bind: { into: 'env', envKey: 'CONFLUENCE_USERNAME' },
          placeholder: 'you@company.com',
          help: 'Usually the same Atlassian account email as Jira.',
        },
        {
          key: 'confluence_api_token',
          label: 'Confluence API token',
          bind: { into: 'env', envKey: 'CONFLUENCE_API_TOKEN' },
          secret: true,
          placeholder: 'Atlassian API token',
          help: 'Same id.atlassian.com API-tokens page. You can reuse the Jira token if it has access to both products.',
        },
      ],
    },
  },
  {
    id: 'linear',
    label: 'Linear',
    description: 'Search issues, view cycles, and update project status in Linear.',
    category: 'productivity',
    icon: '🔺',
    source: 'Linear',
    template: {
      suggestedName: 'linear',
      transport: 'sse',
      url: 'https://mcp.linear.app/sse',
      requiresAuth: true,
      authLabel: 'Linear API key',
      fields: [
        {
          key: 'token',
          label: 'Linear API key',
          bind: { into: 'authToken' },
          secret: true,
          required: true,
          placeholder: 'lin_api_… or a Secrets Manager ARN',
          help: 'Linear → Settings → API → Personal API keys. Paste the key, or a Secrets Manager ARN to store it securely.',
        },
      ],
    },
  },
  {
    id: 'slack',
    label: 'Slack',
    description: 'Post messages, read channels, and search conversations in your Slack workspace.',
    category: 'productivity',
    icon: '💬',
    source: 'Slack',
    template: {
      suggestedName: 'slack',
      transport: 'stdio',
      command: 'npx',
      args: ['-y', '@modelcontextprotocol/server-slack'],
      requiresAuth: true,
      authLabel: 'Slack bot token',
      fields: [
        {
          key: 'bot_token',
          label: 'Slack bot token',
          bind: { into: 'env', envKey: 'SLACK_BOT_TOKEN' },
          secret: true,
          required: true,
          placeholder: 'xoxb-…',
          help: 'Create a Slack app, add bot scopes, install it to your workspace, then copy the Bot User OAuth Token.',
        },
        {
          key: 'team_id',
          label: 'Slack team ID',
          bind: { into: 'env', envKey: 'SLACK_TEAM_ID' },
          required: true,
          placeholder: 'T01234567',
          help: 'Your workspace ID (starts with T). Find it in Slack workspace settings, or in any channel URL.',
        },
      ],
    },
  },
];
